/* eslint-disable no-unused-vars */
import { useState, useId, useEffect, useRef, useMemo } from 'react';
import { globalAPI } from 'services/global.service';
import { userTypes } from 'constants';
import { CustomButton, Loader, DropDownSelect, SwitchUserItem, Buttons } from 'components';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useDebounce, replaceNumberInUrl } from 'utils';
import { Palette, routes } from 'config';
import { authActions, ssoLogin } from 'store/auth/auth';
import { authAPI } from 'services/newPlatformAuth.service';
import { useSignal } from '@preact/signals-react';

const handleClickOutSide = (e, isOpen$) => {
  const clickToDropDownOrInput = e.target.closest(`.inputAndSearchList`);
  if (!clickToDropDownOrInput) {
    isOpen$.value = false;
  }
};

export const LoginPublisherList = ({
  authGoogleKey,
  setIsGoogleLogin,
  setToken,
  children,
  getPublisherLink,
}) => {
  const [trigger, result] = globalAPI.useLazyGetPublisherListQuery();

  const [logoutAction, logoutResult] = authAPI.useAdminLogoutMutation();

  const dispatch = useDispatch();

  const [inputValue, setInputValue] = useState('');

  const isOpen$ = useSignal(false);
  const debouncedValue = useDebounce(inputValue, 1000);

  const handleInputValue = (e) => {
    setInputValue(e.target.value);
  };

  const handleLogout = () => {
    //document.cookie = `${authGoogleKey}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    document.cookie = `${authGoogleKey}=; domain=.${window.location.hostname}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
    setIsGoogleLogin(false);
    setToken('');
    logoutAction();
  };

  useEffect(() => {
    if (debouncedValue.length > 3) {
      trigger(debouncedValue);
    }
  }, [debouncedValue]);

  const publisherItemClick = (pb_number) => {
    const selectedPublisher = result.data.find((pubData) => pubData.pb_number === pb_number);
    dispatch(authActions.addPublisherToList(selectedPublisher));
    isOpen$.value = false;
  };

  const showDropDownList = useMemo(() => {
    return result.isSuccess && result?.data?.length;
  }, [result.isSuccess, result?.data?.length]);

  const showDropDownEmptyList = useMemo(() => {
    return result.isSuccess && !result?.data?.length;
  }, [result.isSuccess, result?.data?.length]);

  useEffect(() => {
    if (showDropDownList || showDropDownEmptyList) {
      window.addEventListener('click', (e) => handleClickOutSide(e, isOpen$), { capture: true });
    }
    if (!showDropDownList && !showDropDownEmptyList) {
      window.removeEventListener('click', handleClickOutSide);
    }
    return () => {
      window.removeEventListener('click', handleClickOutSide);
    };
  }, [showDropDownList, showDropDownEmptyList]);

  const isEmptyList = () => {
    if (result.data && result.data.length === 0) {
      return true;
    }
    return false;
  };

  return (
    <InputBox>
      {result.isFetching && <Loader.Global delay={1000} />}
      <Container>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Title>Search for an account</Title>
          <Buttons.PrimaryNormal id='logoutButton' onClick={handleLogout}>
            Logout
          </Buttons.PrimaryNormal>
        </div>
        <div className='inputAndSearchList'>
          <DropDownSelect
            isDynamicChildren={true}
            id='inputAndSearchList_id'
            search={true}
            placeholder={'Please select an account or search for one'}
            activeElem={null}
            onSearch={handleInputValue}
            searchValue={inputValue}
            onResetValue={() => {
              setInputValue('');
            }}
          >
            {isEmptyList() ? (
              <NoPublishers>no Publishers</NoPublishers>
            ) : (
              result?.data?.map((user) => {
                const { pb_company, pb_email, pb_id, pb_number, selected } = user;
                return (
                  <div key={pb_number} style={{ width: '902px' }}>
                    <SwitchUserItem
                      {...user}
                      key={pb_number}
                      getLink={getPublisherLink}
                      onClick={(e) => {
                        e.preventDefault();
                        publisherItemClick(pb_number);
                        setInputValue('');
                        const url = getPublisherLink(pb_number);
                        window.open(url, '_blank');
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setInputValue('');
                        publisherItemClick(pb_number);
                        const url = getPublisherLink(pb_number);
                        window.location.href = url;
                      }}
                      userType={user.userType}
                      searchValue={inputValue}
                    />
                  </div>
                );
              })
            )}
          </DropDownSelect>
        </div>
      </Container>
      <ListBox>
        <Title>Your latest accounts search history</Title>
        {children}
      </ListBox>
    </InputBox>
  );
};

const NoPublishers = styled.div`
  width: 902px;
  pointer-events: none;
  height: 30px;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ListBox = styled.div`
  width: 1000px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0px 1px 3px 0px rgba(30, 50, 70, 0.1), 0px 1px 2px 0px rgba(30, 50, 70, 0.06);
  margin-top: 8px;
  padding: 32px 40px;
`;

const Title = styled.span`
  color: ${Palette.PrimaryGray._900};
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 32px;
`;

const InputBox = styled.div`
  position: relative;
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Container = styled.div`
  width: 1000px;
  display: flex;
  flex-direction: column;
  gap: 32px;

  padding: 32px 40px;
  background: white;
  border-radius: 8px;
  box-shadow: 0px 1px 3px 0px rgba(30, 50, 70, 0.1), 0px 1px 2px 0px rgba(30, 50, 70, 0.06);
`;

export const Initials = styled.div`
  min-width: 40px;
  min-height: 40px;
  border-radius: 50%;
  color: #ffffff;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 16px;
  width: 10%;
  background: ${({ userType }) => getColor(userType)};
`;

const getColor = (status) => {
  if (status === userTypes.advertiser) return Palette.Purple._500;
  if (status === userTypes.publisher) return Palette.Primary._500;
  if (status === userTypes.admin) return Palette.Red._500;
  return Palette.Primary._500;
};
